/**
 * Document List Page
 * Role-aware document listing with search, filter, and actions
 * - User: sees own documents
 * - Reviewer: sees assigned documents
 * - Admin: sees all documents
 */

import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Button } from '@/components/ui/button';
import {
  FilePlus,
  Eye,
  Edit,
  Trash2,
  Send,
} from 'lucide-react';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useDocuments } from '@/hooks/useDocuments';
import { ROUTES, getDocumentEditorRoute } from '@/constants/routes';
import { DOCUMENT_STATUS, formatDate } from '@/constants/beStudyMockData';
import { DataTable } from '@/components/be-study/common/DataTable';
import { DocumentStatusBadge } from '@/components/be-study/common/StatusBadge';
import { SimpleProgressBar } from '@/components/be-study/common/ProgressBar';
import { ConfidenceBadge } from '@/components/be-study/common/ConfidenceIndicator';
import { ConfirmationModal } from '@/components/be-study/modals/ConfirmationModal';

export function DocumentList() {
  const { currentUser, isAdmin, isReviewer, isUser } = useAuth();
  const { documents, deleteDocument } = useDocuments();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = React.useState(null);

  // Filter docs by role
  const filteredDocs = useMemo(() => {
    if (isAdmin) return documents;
    if (isReviewer) return documents.filter(d => d.assignedReviewer === currentUser?.id);
    return documents.filter(d => d.createdBy === currentUser?.id);
  }, [documents, currentUser, isAdmin, isReviewer]);

  const pageTitle = isReviewer ? 'Documents to Review' : isAdmin ? 'All Documents' : 'My Documents';

  const columns = [
    {
      accessorKey: 'title',
      header: 'Document',
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="text-sm font-medium truncate max-w-[280px]">{row.title}</p>
          <p className="text-xs text-muted-foreground">{row.templateName}</p>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ value }) => <DocumentStatusBadge status={value} size="sm" />,
    },
    {
      accessorKey: 'progress',
      header: 'Progress',
      cell: ({ value }) => (
        <div className="w-24">
          <SimpleProgressBar value={value} size="sm" />
        </div>
      ),
    },
    {
      accessorKey: 'confidenceScore',
      header: 'Confidence',
      cell: ({ value }) => value > 0 ? <ConfidenceBadge score={value} size="sm" /> : <span className="text-xs text-muted-foreground">-</span>,
    },
    {
      accessorKey: 'createdByName',
      header: 'Author',
      cell: ({ value }) => <span className="text-sm">{value}</span>,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last Updated',
      cell: ({ value }) => <span className="text-sm text-muted-foreground">{formatDate(value)}</span>,
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ value }) => {
        if (!value) return <span className="text-xs text-muted-foreground">-</span>;
        const isOverdue = new Date(value) < new Date();
        return (
          <span className={`text-sm ${isOverdue ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
            {formatDate(value)}
          </span>
        );
      },
    },
  ];

  const filters = [
    {
      id: 'status',
      label: 'Status',
      options: Object.entries(DOCUMENT_STATUS).map(([key, value]) => ({
        value,
        label: value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      })),
    },
  ];

  const handleDelete = (doc) => {
    setDeleteTarget(doc);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteDocument(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const rowActions = (row) => (
    <>
      <DropdownMenuItem
        onClick={() => navigate(getDocumentEditorRoute(row.id))}
        data-testid={`view-doc-${row.id}`}
      >
        <Eye className="h-4 w-4 mr-2" /> View
      </DropdownMenuItem>
      {(isAdmin || (isUser && row.createdBy === currentUser?.id)) && (
        <DropdownMenuItem
          onClick={() => navigate(getDocumentEditorRoute(row.id))}
          data-testid={`edit-doc-${row.id}`}
        >
          <Edit className="h-4 w-4 mr-2" /> Edit
        </DropdownMenuItem>
      )}
      {(isAdmin || (isUser && row.createdBy === currentUser?.id)) && row.status === DOCUMENT_STATUS.DRAFT && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-600"
            onClick={() => handleDelete(row)}
            data-testid={`delete-doc-${row.id}`}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  return (
    <BaseLayout title={pageTitle}>
      <div className="p-6" data-testid="document-list-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl font-bold" data-testid="document-list-title">{pageTitle}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}
            </p>
          </div>
          {(isAdmin || isUser) && (
            <Button asChild data-testid="create-document-btn">
              <Link to={ROUTES.DOCUMENT_CREATE}>
                <FilePlus className="h-4 w-4 mr-2" />
                Create Document
              </Link>
            </Button>
          )}
        </div>

        <DataTable
          data={filteredDocs}
          columns={columns}
          filters={filters}
          searchPlaceholder="Search documents..."
          searchKeys={['title', 'templateName', 'createdByName']}
          onRowClick={(row) => navigate(getDocumentEditorRoute(row.id))}
          rowActions={rowActions}
          emptyMessage="No documents found"
          initialPageSize={10}
        />

        {/* Delete Confirmation */}
        <ConfirmationModal
          open={!!deleteTarget}
          onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
          onConfirm={confirmDelete}
          title="Delete Document"
          description={deleteTarget ? `Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.` : ''}
          confirmText="Delete"
          type="danger"
        />
      </div>
    </BaseLayout>
  );
}

export default DocumentList;
